import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import ButtonAuth from '../../components/ButtonAuth';
import rocketBoyImg from '../../assets/images/barbfullLogo.png';
import Input from '../../components/Input';
import { Controller, useForm } from 'react-hook-form';
import useClient from '../../hooks/useClient';
import Alert from '../../components/Alert';
import Cookies from 'js-cookie';
import config from '../../config';
import AuthWrapper from '../../components/AuthWrapper';
import AuthHeader from '../../components/header/AuthHeader';
import { useTrackingContext } from '../../context/trackingContext';
import BannerRight from '../../components/BannerRight';
import Footer from '../../components/Footer';
import { useUserContext } from '../../context/userContext';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import SelectInput from '../../components/SelectInput';
import { langOptions, monthOptions, yearOptions } from '../../mock';

const isValidDate = (values) => {
  const { day, month, year } = values;

  if (day && month && year) {
    return moment(`${year.value}-${month.value}-${day.value}`, 'YYYY-MM-DD').isValid();
  }
  return true;
};

const calculateDaysInMonth = (month, year) => {
  return new Date(year, month, 0).getDate();
};

function StepTwo() {
  const {
    trackFormStart,
    trackClick,
    trackPageView,
    trackFormSubmit,
    setEmail,
    tracking,
    trackError,
    trackSignUp,
    utmData,
    setInternal
  } = useTrackingContext();
  const { legacyId, referralId } = useUserContext();
  const { search } = useLocation();
  const [dayOptions, setDayOptions] = useState([]);
  const [age, setAge] = useState(null);
  const [dob, setDob] = useState(null);
  const isAgeBetween13and16 = age >= 13 && age < 16;
  const isUnder13 = age < 13 && age != null;

  const {
    register,
    control,
    handleSubmit,
    watch,
    trigger,
    getValues,
    setValue,
    formState: { errors, isSubmitting, isValid, isDirty }
  } = useForm({ mode: 'onTouched' });

  const navigate = useNavigate();
  const location = useLocation();
  const client = useClient();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [showAlert, setShowAlert] = useState(false);

  const updateDayOptions = (month, year) => {
    const daysInMonth = calculateDaysInMonth(month, year);
    const options = Array.from({ length: daysInMonth }, (_, i) => ({ label: i + 1, value: i + 1 }));
    setDayOptions(options);
  };

  const onSubmit = async (data) => {
    trackFormSubmit({
      formName: 'Account credentials form Submit',
      accountCreationStep: 2.3,
      emailSignup: data.receiveEmails,
      referralId
    });

    setEmail(data.email);

    const captchaToken = await handleReCaptchaVerify();

    try {
      const response = await client.post(`${config.authAPI}/users/create`, {
        username: data.username,
        emailAddress: data.email,
        password: data.password,
        dob,
        language: data.language?.value ?? 'en',
        parentalEmail: data.parentalEmail,
        referrer: 'Website',
        emailNewsletter: data.receiveEmails,
        legacyId,
        referralId,
        utmData,
        captchaToken
      });

      const params = new URLSearchParams(location.search);

      const loginResponse = await client.post(`${config.everywhereApi}/users/login`, {
        email: data.email,
        password: data.password,
        isSupport: params.has('brand_id')
      });

      const { accessToken } = loginResponse.data.data;

      Cookies.set('_barbToken', accessToken, { expires: 1 / 36 }); // 40 minutes
      setInternal(response.data.internal);

      try {
        trackSignUp();
      } catch (e) {
        console.error("Couldn't track sign up", e);
      }

      navigate('/verify-email');
    } catch (err) {
      console.error(err);
      const error = err?.response?.data?.error;

      try {
        trackError({ error_text: error.errorMessage });
      } catch (e) {
        console.error("Couldn't track error", e);
      }

      setShowAlert({
        text: error ? error?.errorMessage + ' - ' + error.errorCode : 'Unknown error',
        success: false
      });
    }
  };

  const startsAndEndsWithAlphaNumeric = (string) =>
    /^[a-z0-9]+$/i.test(string.charAt(0)) && /^[a-z0-9]+$/i.test(string.charAt(string.length - 1));

  // uppercase and lowercase letters, spaces, special characters
  const containsValidUsernameCharacters = (string) =>
    !/^[a-zA-Z0-9]+(([',. -][a-zA-Z0-9])?[a-zA-Z0-9]*)*$/.test(string);

  useEffect(() => {
    if (watch('email')) trigger('parentalEmail');
  }, [watch('email')]);

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }

    return executeRecaptcha('accountcreate');
  }, [executeRecaptcha]);

  useEffect(() => {
    const defaultLanguage = navigator.language || navigator.userLanguage;
    const selectedLanguage = langOptions.find(
      (option) => option.value === defaultLanguage.substring(0, 2)
    );
    setValue('language', selectedLanguage);

    if (!tracking) return;

    const query = new URLSearchParams(search);
    const legacyId = query.get('lid');
    const referralId = query.get('referral_id');

    trackPageView({
      pageName: 'Account creation page visit',
      accountCreationStep: 2.1,
      legacyId,
      referralId
    });
    document.getElementById('left-container')?.scrollTo(0, 0);
  }, [tracking]);

  useEffect(() => {
    isDirty &&
      trackFormStart({
        formName: 'Account credentials form Start',
        accountCreationStep: 2.2
      });
  }, [isDirty]);

  useEffect(() => {
    const month = watch('month')?.value;
    const year = watch('year')?.value;
    if (month && year) {
      updateDayOptions(month, year);
      trigger('day');
    }
  }, [watch('month'), watch('year'), watch('day')]);

  useEffect(() => {
    const month = watch('month')?.value;
    const year = watch('year')?.value;
    const day = watch('day')?.value;
    if (day && month && year) {
      const selectedDate = new Date(year, month - 1, day);
      const today = new Date();
      const ageDiffMs = today - selectedDate;
      const ageDate = new Date(ageDiffMs);
      const age = Math.abs(ageDate.getUTCFullYear() - 1970);
      setAge(age);

      setDob(month + '-' + day + '-' + year);
    }
  }, [watch('month'), watch('year'), watch('day')]);

  return (
    <div className="flex h-screen">
      <div id="left-container" className="w-full overflow-y-scroll lg:w-1/2 relative left-form">
        <AuthHeader />
        <AuthWrapper className="max-w-sm mb-4">
          <form
            className="mb-6"
            id="step-two-first"
            autoComplete="false"
            onSubmit={handleSubmit(onSubmit)}>
            <h1 className="font-Korolev-Bold text-5xl xl:text-5xl uppercase mb-5">
              create <br /> account
            </h1>

            <img src={rocketBoyImg} alt="build rocket boy" className="w-full mb-8" />

            {isUnder13 ? (
              <div id="unable-crete-account" className="py-10">
                <h6 className="font-Korolev-Bold text-xl text-black text-center uppercase m-1">
                  UNABLE TO CREATE ACCOUNT
                </h6>
                <p className="text-black text-center font-Korolev-Medium text-base mb-5">
                  We are currently unable to process your account request at this time.
                </p>
              </div>
            ) : (
              <>
                <div className={`mb-8 lg:mb-8`}>
                  <label htmlFor="" className={`mb-1 block`}>
                    DATE OF BIRTH
                  </label>
                  <div className="flex justify-between items-center gap-3">
                    <div className="w-1/3">
                      <Controller
                        autoFocus
                        control={control}
                        name="year"
                        className="w-1/3"
                        rules={{ required: 'Year is required' }}
                        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                          <SelectInput
                            options={yearOptions}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            placeholder="YYYY"
                            className="w-full"
                            error={error}
                            errorClassName="absolute"
                          />
                        )}
                      />
                    </div>
                    <div className="w-1/3">
                      <Controller
                        control={control}
                        name="month"
                        className="w-1/3"
                        rules={{ required: 'Month is required' }}
                        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                          <SelectInput
                            options={monthOptions}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            placeholder="MON"
                            className="w-full"
                            error={error}
                            errorClassName="absolute"
                          />
                        )}
                      />
                    </div>
                    <div className="w-1/3">
                      <Controller
                        control={control}
                        name="day"
                        className="w-1/3"
                        rules={{
                          required: 'Day is required',
                          validate: () => (!isValidDate(getValues()) ? 'Invalid Day' : true)
                        }}
                        render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                          <SelectInput
                            options={dayOptions}
                            value={value}
                            onChange={onChange}
                            onBlur={onBlur}
                            placeholder="DAY"
                            className="w-full"
                            error={error}
                            errorClassName="absolute"
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <Controller
                    defaultValue=""
                    control={control}
                    name="username"
                    rules={{
                      required: 'Username is required',
                      minLength: {
                        value: 3,
                        message: 'Username must be at least 3 characters long'
                      },
                      maxLength: {
                        value: 25,
                        message: 'Username must be at most 25 characters long'
                      },
                      validate: (value) =>
                        !startsAndEndsWithAlphaNumeric(value)
                          ? 'Username should start and end with alphanumeric'
                          : containsValidUsernameCharacters(value)
                          ? "Username can only contain alphanumeric, spaces and non-repeating special characters: , . ' -"
                          : true
                    }}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                      <Input
                        type="text"
                        label="Username"
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        className="w-full"
                        questionIcon={true}
                        placeholder="Other players will see this name."
                        tooltip={
                          <div className="text-sm">
                            <p>Do not use your real name or identifying information.</p>
                          </div>
                        }
                        error={error}
                      />
                    )}
                  />
                </div>

                <div className="mb-4">
                  <Controller
                    defaultValue=""
                    control={control}
                    name="password"
                    rules={{
                      required: 'Password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters long'
                      },
                      maxLength: {
                        value: 32,
                        message: 'Password must be at most 32 characters long'
                      },
                      pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
                        message:
                          'Password must contain at least one lowercase letter, one uppercase letter, one number, and one of the following special characters: @,$,!,%,*,?, and &. Other special characters are not accepted.'
                      }
                    }}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                      <Input
                        type="password"
                        label="Password"
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        className="w-full"
                        passwordVisiblity
                        questionIcon={true}
                        tooltip={
                          <>
                            <h2 className="font-Korolev-Bold text-base">PASSWORD REQUIREMENTS:</h2>
                            <ul className="list-disc text-[13px] pl-2.5 list-inside marker:text-lg">
                              <li>
                                <span className="-ml-2">1 Letter, 1 Capital Letter, 1 Number</span>
                              </li>
                              <li>
                                <span className="-ml-2">
                                  1 Special Character: @, $, !, %, *, ? and & only
                                </span>
                              </li>

                              <li>
                                <span className="-ml-2">
                                  No character may repeat more than 2 times
                                </span>
                              </li>

                              <li>
                                <span className="-ml-2">Between 8 and 32 characters in length</span>
                              </li>
                            </ul>
                          </>
                        }
                        error={error}
                      />
                    )}
                  />
                </div>

                <div className="mb-4">
                  <Controller
                    defaultValue=""
                    control={control}
                    name="rePassword"
                    rules={{
                      required: true,
                      validate: (value) => value === watch('password') || 'Passwords do not match'
                    }}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                      <Input
                        type="password"
                        label="Confirm Password"
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        className="w-full"
                        questionIcon={false}
                        passwordVisiblity
                        error={error}
                      />
                    )}
                  />
                </div>

                <div className="mb-4">
                  <Controller
                    defaultValue=""
                    control={control}
                    name="email"
                    rules={{
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Entered value does not match email format'
                      }
                    }}
                    render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                      <Input
                        type="email"
                        value={value}
                        onChange={onChange}
                        onBlur={onBlur}
                        label="Email Address"
                        className="w-full"
                        error={error}
                      />
                    )}
                  />
                </div>

                {isAgeBetween13and16 && (
                  <div className="mb-4">
                    <Controller
                      defaultValue=""
                      control={control}
                      name="parentalEmail"
                      rules={{
                        required: 'Parent/Guardian Email is required',
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message: 'Entered value does not match email format'
                        },
                        validate: (value) => value !== watch('email') || 'Emails cant not match'
                      }}
                      render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
                        <Input
                          type="email"
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          label="Parent/Guardian's Email Address"
                          className="w-full"
                          error={error}
                        />
                      )}
                    />
                  </div>
                )}

                <div className="mb-5">
                  <label className="font-Korolev-Light leading-5 items-start">
                    <input
                      type="checkbox"
                      className="mr-2 mt-1.5 accent-black"
                      {...register('ageConsent', { required: 'User Agreement is required.' })}
                    />
                    {isAgeBetween13and16 ? (
                      <>
                        I confirm am between 13 and 16 years old (or other minimum age of consent to
                        access general audience sites in my country) and my parent(s) or legal
                        guardian(s) have previously agreed to the{' '}
                        <a
                          className="underline"
                          target="blank"
                          href={'https://www.buildarocketboy.com/terms-of-use'}>
                          Terms of Use
                        </a>{' '}
                        and acknowledged the{' '}
                        <a
                          className="underline"
                          target="blank"
                          href={'https://www.buildarocketboy.com/privacy-notice'}>
                          Privacy Notice
                        </a>
                        .
                      </>
                    ) : (
                      <>
                        I confirm that I am at least 16 years of age. I agree and accept the{' '}
                        <a
                          className="underline"
                          target="blank"
                          href={'https://www.buildarocketboy.com/terms-of-use'}>
                          Terms of Use
                        </a>{' '}
                        and acknowledged the{' '}
                        <a
                          className="underline"
                          target="blank"
                          href={'https://www.buildarocketboy.com/privacy-notice'}>
                          Privacy Notice
                        </a>
                        .
                      </>
                    )}
                  </label>
                  <br />
                  {errors.ageConsent && (
                    <span className="text-red text-sm">{errors.ageConsent.message}</span>
                  )}
                </div>

                <div className="mb-7">
                  <label className="font-Korolev-Light leading-5 items-start">
                    <input
                      type="checkbox"
                      className=" mr-2 mt-1.5 accent-black	"
                      {...register('receiveEmails')}
                    />{' '}
                    {`I want to receive emails from ${
                      age >= 18 ? 'MindsEye and' : ''
                    } Build A Rocket Boy.`}
                  </label>
                  {errors.receiveEmails && (
                    <span className="text-red text-sm">{errors.receiveEmails.message}</span>
                  )}
                </div>

                <ButtonAuth
                  label={'CREATE ACCOUNT'}
                  className="mb-3 lg:mb-7"
                  type="submit"
                  isSubmitting={isSubmitting}
                  isDisabled={!isValid}
                />

                <Link
                  to="/login"
                  onClick={() => trackClick({ btnName: 'Log In' })}
                  className="text-black text-center underline border-b-black inline-block my-5">
                  Already have an account? Log in.
                </Link>
              </>
            )}

            <div className="mt-10 font-Korolev-Light text-sm italic">
              This site is protected by reCAPTCHA and the Google{' '}
              <a className="underline" target="blank" href="https://policies.google.com/privacy">
                Privacy Policy
              </a>{' '}
              and{' '}
              <a className="underline" target="blank" href="https://policies.google.com/terms">
                Terms of Service
              </a>{' '}
              apply.
            </div>
          </form>
          {showAlert && (
            <Alert setShowAlert={setShowAlert} success={false}>
              <p className="pr-12 text-sm md:text-base">Create Account failed: {showAlert.text}</p>
            </Alert>
          )}
        </AuthWrapper>
        <Footer />
      </div>
      <BannerRight />
    </div>
  );
}

export default StepTwo;
